<template>
  <div class="flex-grow-1">
    <a-checkbox v-model="isEnableImageAnalytics">
      Enable Image Analytics
      <a-tooltip placement="top">
        <template slot="title">
          <span class="fs-10">
            Enable Image Analytics
          </span>
        </template>
        <a-icon type="info-circle" theme="filled" />
      </a-tooltip>
    </a-checkbox>
    <div class="image-threshold bg-description radius-4-px my-20-px p-20-px mb-23-px">
      <p class="label font-weight-bold">
        Image Threshold:
        <span v-if="!isValidImageThreshold" class="notice">
          <small>
            Please input from 0 to 100
          </small>
        </span>
      </p>
      <div>
        <a-input class="input-percentage" suffix="%" placeholder="Enter 0 - 100" :disabled="!isEnableImageAnalytics" :value="thresholdImageSimilarity" @change="onChangeThresholdPercentage" />
        <a-button size="large" type="primary" :disabled="!isEnableImageAnalytics || !isValidImageThreshold" @click="onAddRegions">
          <img class="mr-2 icon-size" src="@assets/icon/plus.svg" />
          Add / Edit Regions
        </a-button>
      </div>
    </div>
    <div ref="imageAnalyticsContainer" class="image-analytics-container"></div>
    <div v-if="!webpageScreenShot">
      {{ screenshotMissingMessage }}
    </div>
  </div>
</template>

<script>
  const ImageMaxWidth = 650;
  import { isCanvasSupported } from '@shared/helpers/utils';
  import WebpageService from '../services/webpage.service';
  import { BASELINE_SCREENSHOT_MISSING } from '../consts/Webpage.consts';

  export default {
    name: 'ImageAnalytics',
    components: {},
    data() {
      return {
        isCanvasSupported,
        isEnableImageAnalytics: false,
        webpageScreenShot: '',
        imageRegions: [],
        thresholdImageSimilarity: 0,
        isBrowserSupportBroadCast: false,
        imageRegionBroadcastChannel: null,
        isValidImageThreshold: true,
        screenshotMissingMessage: BASELINE_SCREENSHOT_MISSING,
      };
    },
    beforeCreate() {
      this.isBrowserSupportBroadCast = 'BroadcastChannel' in window;
    },
    mounted() {
      if (this.isBrowserSupportBroadCast) {
        this.imageRegionBroadcastChannel = new BroadcastChannel('image-region-channel');
        this.imageRegionBroadcastChannel.onmessage = () => {
          this.getImagesAnalyticsData();
        };
      }
      this.getImagesAnalyticsData();
    },
    beforeDestroy() {
      this.imageRegionBroadcastChannel && this.imageRegionBroadcastChannel.close();
    },
    methods: {
      onAddRegions() {
        if (this.isEnableImageAnalytics) {
          this.$confirm({
            title: 'You will be redirected to another page. Please save your configurations before proceeding.',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: () => {
              this.$router.push({
                // Note, we use the absolute path /webpages/xxx here because this route needs to work on both
                // regular webpages and domain webpages
                path: `/webpages/${this.$route.params.id}/image-region`,
                query: { threshold: this.thresholdImageSimilarity },
              });
            },
          });
        }
      },
      async getImagesAnalyticsData() {
        const webpageId = this.$route.params && this.$route.params.id;
        try {
          const [webpageData, dataScreenshot] = await Promise.all([WebpageService.get(+webpageId), WebpageService.getImagesAnalytics(+webpageId)]);
          this.webpageScreenShot = dataScreenshot.screenshot;
          this.imageRegions = webpageData.image_regions || [];
          this.isEnableImageAnalytics = webpageData.threshold && webpageData.threshold.enable_image_engine;
          this.thresholdImageSimilarity = (webpageData.threshold && webpageData.threshold.image_similarity) || 0;
          this.drawImage();
        } catch {
          //this.$showErrorMessage('HTTP error received from web server.!');
        }
      },
      drawImage() {
        const imgElement = new Image();
        imgElement.src = this.webpageScreenShot;
        imgElement.alt = 'Image analytics';
        setTimeout(() => {
          const percentScale = ImageMaxWidth / imgElement.width;
          if (this.isCanvasSupported) {
            let canvasElement = this.$refs.imageAnalyticsContainer.querySelector('CANVAS');
            if (!canvasElement) {
              canvasElement = document.createElement('CANVAS');
            }
            const ctx = canvasElement.getContext('2d');
            ctx.canvas.width = imgElement.width;
            ctx.canvas.height = imgElement.height;
            ctx.drawImage(imgElement, 0, 0, imgElement.width, imgElement.height, 0, 0, ImageMaxWidth, imgElement.height * percentScale);
            for (const iterator of this.imageRegions) {
              ctx.beginPath();
              ctx.fillStyle = 'rgba(255, 62, 62, 0.2)';
              ctx.fillRect(iterator.coord_x * percentScale, iterator.coord_y * percentScale, iterator.coord_w * percentScale, iterator.coord_h * percentScale);
              ctx.stroke();
              ctx.beginPath();
            }
            this.$refs.imageAnalyticsContainer.appendChild(canvasElement);
          } else {
            imgElement.style.maxWidth = `${ImageMaxWidth}px`;
            this.$refs.imageAnalyticsContainer.appendChild(imgElement);
            for (const [index, iterator] of this.imageRegions.entries()) {
              const selectedAreaElement = document.createElement('DIV');
              selectedAreaElement.style.position = 'absolute';
              selectedAreaElement.style.zIndex = `${index + 3}`;
              selectedAreaElement.style.backgroundColor = 'rgba(255, 62, 62, 0.2)';
              selectedAreaElement.style.top = `${iterator.coord_y * percentScale}px`;
              selectedAreaElement.style.left = `${iterator.coord_x * percentScale}px`;
              selectedAreaElement.style.width = `${iterator.coord_w * percentScale}px`;
              selectedAreaElement.style.height = `${iterator.coord_h * percentScale}px`;
              this.$refs.imageAnalyticsContainer.appendChild(selectedAreaElement);
            }
          }
        });
      },
      onChangeThresholdPercentage(inputEvent) {
        const valueAsNumber = +inputEvent.target.value;
        if (isNaN(valueAsNumber)) {
          this.isValidImageThreshold = false;
          inputEvent.preventDefault();
          return;
        }
        if (valueAsNumber < 0) {
          this.isValidImageThreshold = false;
          inputEvent.preventDefault();
          return;
        }
        if (valueAsNumber > 100) {
          this.isValidImageThreshold = false;
          inputEvent.preventDefault();
          return;
        }
        this.thresholdImageSimilarity = valueAsNumber;
        this.isValidImageThreshold = true;
        this.$emit('changeThreshold', valueAsNumber);
      },
    },
  };
</script>
<style lang="scss" scoped>
  .image-threshold {
    overflow: hidden;
    .label {
      margin-bottom: 0;
      padding: 0;
    }
    .input-percentage {
      margin-right: 0.5rem;
      width: max-content;
    }
  }
  .image-analytics-container {
    width: max-content;
    position: relative;
  }
  .notice {
    color: #ff0000;
    font-style: normal;
    font-weight: 400;
  }
</style>
